// The title's night clearing (docs/18 mockup "Main menu"): a starry sky band, a tree line of the
// forest kit drawn through the view-tile ruleset (game/tiling) with its top row standing against
// the sky, a grass clearing with the Oryx campfire in the middle and four heroes around it. The
// Play online hub reuses it dimmed. Whole-number scale only (3×); embers rise from the fire.
import { useEffect, useMemo, useRef, useState } from 'react';
import { HeroSprite, loadWorldAtlas, terrainFromRows, useReducedMotion } from '../components';
import { kitFor, tileBoard, oryx } from '../../game/tiling';
import { classLook, type HeroLook } from '../../game/heroKit';

const SCALE = 3, CELL = 24;
// 31 columns, centred on the window; row 0 stands against the sky (no ground drawn), gaps are sky.
const ROWS = [
  'TTT.TTTT.TTTT.TT.TTTTT.TTT.TTTT',
  'TTTTTTTTTTTTTTTTTTTTTTTTTTTTTTT',
  'TT.TTT..T...........TT...TTT.TT',
  'T,.......................,....T',
  '..,..........................,.',
  '...............................',
  '...............................',
  '...............................',
  '...............................',
  ',..............................',
  '...........,..........,......,.',
  '...............................',
  '...............................',
];
const SKY_ROWS = 1, LIP = 5;
const SEED = 7;   // picked by eye: a clear middle, oaks and a pine stand
const STARS = Array.from({ length: 64 }, (_, i) => ({ left: (i * 61 + 7) % 100, top: (i * 37 + 3) % 92, size: i % 7 === 0 ? 3 : 2, tw: i % 5 === 0 }));
const EMBERS = Array.from({ length: 9 }, (_, i) => ({ dx: ((i * 13) % 30) - 15, delay: `${(i * 0.9) % 6}s`, dur: `${5 + (i % 4)}s` }));

export interface ClearingHero { key: string; frame?: number; classId?: string; title?: string; heroId?: bigint; species?: number; look?: HeroLook }
// around the fire: upper-left, lower-left (facing right), upper-right, lower-right (mirrored)
const SPOTS = [{ x: -111, y: -45, flip: false }, { x: -147, y: 33, flip: false }, { x: 96, y: -45, flip: true }, { x: 132, y: 33, flip: true }];

function useForestCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  const board = useMemo(() => terrainFromRows(ROWS, SEED), []);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let live = true;
    void loadWorldAtlas().then((atlas) => {
      const c = ref.current; const ctx = c?.getContext('2d');
      if (!live || !atlas || !ctx) return;
      const tiled = tileBoard(board, kitFor('forest'));
      ctx.imageSmoothingEnabled = false;
      for (const layer of ['ground', 'overlay', 'props'] as const) {
        for (let y = 0; y < tiled.h; y++) for (let x = 0; x < tiled.w; x++) {
          if (y < SKY_ROWS && layer === 'overlay') continue;
          for (const v of tiled.cells[y * tiled.w + x]![layer]) {
            const f = atlas.frames[v.frame];
            if (!f) continue;
            // the sky row keeps only a grass lip under the trunks; the tree tops stand against the stars
            const lip = y < SKY_ROWS && layer === 'ground' ? LIP : f.h;
            ctx.drawImage(atlas.img, f.x, f.y + f.h - lip, f.w, lip, x * CELL, y * CELL + (CELL - lip), f.w, lip);
          }
        }
      }
      setReady(true);
    });
    return () => { live = false; };
  }, [board]);
  return { ref, board, ready };
}

/** The Oryx campfire prop (`PROPS.fire`, world atlas), two frames. */
function Campfire() {
  const reduced = useReducedMotion();
  const [atlas, setAtlas] = useState<Awaited<ReturnType<typeof loadWorldAtlas>>>();
  const [phase, setPhase] = useState(0);
  useEffect(() => { let live = true; void loadWorldAtlas().then((a) => { if (live) setAtlas(a); }); return () => { live = false; }; }, []);
  useEffect(() => {
    if (reduced) return;
    const t = window.setInterval(() => setPhase((p) => 1 - p), 280);
    return () => window.clearInterval(t);
  }, [reduced]);
  const f = atlas?.frames[oryx.frame(oryx.PROPS.fire[phase]!)];
  if (!atlas || !f) return <span className="fk-title__fire" />;
  return <span className="fk-title__fire" role="img" aria-label="campfire" style={{ width: f.w * SCALE, height: f.h * SCALE, backgroundImage: 'url(/assets/oryx/world.png)', backgroundPosition: `-${f.x * SCALE}px -${f.y * SCALE}px`, backgroundSize: `${atlas.img.naturalWidth * SCALE}px ${atlas.img.naturalHeight * SCALE}px` }} />;
}

export function NightClearing({ heroes, dim, className = '' }: { heroes: ClearingHero[]; dim?: boolean; className?: string }) {
  const { ref, board, ready } = useForestCanvas();
  return (
    <div className={`fk-title__scene ${dim ? 'fk-title__scene--dim' : ''} ${className}`} aria-hidden="true" data-ready={ready ? 'true' : 'false'}>
      <div className="fk-title__sky">{STARS.map((s, i) => <span key={i} className={`fk-title__star ${s.tw ? 'fk-title__star--tw' : ''}`} style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${(i % 9) * 0.7}s` }} />)}</div>
      <canvas ref={ref} className="fk-title__forest" width={board.w * CELL} height={board.h * CELL} style={{ width: board.w * CELL * SCALE, height: board.h * CELL * SCALE }} />
      <div className="fk-title__camp">
        <span className="fk-title__glow" />
        <Campfire />
        <div className="fk-title__embers">{EMBERS.map((e, i) => <span key={i} className="fk-title__ember" style={{ left: `calc(50% + ${e.dx}px)`, animationDelay: e.delay, animationDuration: e.dur }} />)}</div>
        {heroes.slice(0, 4).map((h, i) => (
          <span key={h.key} className={`fk-title__hero ${SPOTS[i]!.flip ? 'fk-title__hero--flip' : ''}`} style={{ left: SPOTS[i]!.x, top: SPOTS[i]!.y }}>
            <HeroSprite heroId={h.heroId} look={h.heroId === undefined ? h.look ?? classLook(h.classId) : undefined} species={h.species} frame={h.frame} classId={h.classId} scale={3} title={h.title} breathe />
          </span>
        ))}
      </div>
      {dim && <div className="fk-title__veil" />}
    </div>
  );
}
